import React, { useState, ReactElement, Fragment, useEffect } from "react";
import { useDispatch } from 'react-redux'
import type { Dispatch } from 'redux'
import DropZone from 'react-dropzone';
import { Link } from 'react-router-dom';
import { actions as baselineActions } from '@Actions/baselineActions';
import { actions as newProjectWizardActions } from '@Actions/newProjectWizardActions';
import * as types from '@Constants/actionTypes';
import { ImportError, NewProjectData } from '@Types/common';
import Notification from '@Common/Notification';
import './ImportTasks.scss';
import FileChooser from "@Components/Common/FileChooser";
import Icon from "@Components/Common/Icon";

interface ImportTasksProps {
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setProjectData: ((func: React.Dispatch<React.SetStateAction<NewProjectData>>, skipIsDirty?: boolean) => any);
    projectKey: string;
    chooseDiffAddTaskMethod: () => void;
    setWizardSubStep?: (subStep: string) => void; // Nullable
    importType: string;
    selectedBaselineId?: number;
}

const ImportTasks = ({ setIsLoading, setProjectData, projectKey, chooseDiffAddTaskMethod, setWizardSubStep, importType, selectedBaselineId = 0 }: ImportTasksProps): ReactElement => {

    const dispatch: Dispatch = useDispatch();
    const importTemplate = async (templateType: string) => dispatch(baselineActions.importTemplate(templateType));
    const importNewBaseline = async (projectKey: string, file: File, importType: string, baselineId?: number) => dispatch(newProjectWizardActions.import(projectKey, file, baselineId, importType));

    const [showPasteSpecial, setShowPasteSpecial] = useState<boolean>(false);
    const [isDropZoneActive, setIsDropZoneActive] = useState<boolean>(false);
    const [baselineId, setBaselineId] = useState<number>(selectedBaselineId);
    const [importErrors, setImportErrors] = useState<Array<ImportError>>([]);
    const [showRelativeHelp, setShowRelativeHelp] = useState<boolean>(false);
    const [showStaticHelp, setShowStaticHelp] = useState<boolean>(false);

    const hiddenBrowseButton = React.useRef(null);

    useEffect(() => {
        setBaselineId(selectedBaselineId);
    }, [selectedBaselineId]);

    const onDownload = async () => {
        importTemplate(importType)
            .then(error => {
                if (error) {
                    Notification("error", "", error);
                }
            });
    }

    const onFileBrowseChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            doImport(file, importType);
        }
    }

    const onDropFile = (files) => {
        console.log(`onDropFile`, files);
        const droppedFile = (files && files.length > 0) ? files[0] : null;

        if (droppedFile) {
            if (droppedFile.type != "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
                Notification("error", "Invalid File Type", "You must pick an Excel document with .xlsx extension");
                setIsDropZoneActive(false);
            } else {
                doImport(droppedFile, importType);
            }
        }
    }

    const doImport = async (importFile: File, fileImportType: string) => {
        setIsLoading(true);
        setImportErrors([]);

        try {
            const response = await importNewBaseline(projectKey, importFile, fileImportType, baselineId ? baselineId : undefined);
            if (response.type === types.NEW_PROJECT_WIZARD_IMPORT_BASELINE_SUCCESS) {
                if (response.payload.importErrors && response.payload.importErrors.length > 0) {
                    Notification("error", "Validation Errors", "Please correct the errors in the Excel document and try again");
                    setImportErrors(response.payload.importErrors);
                    setIsLoading(false);
                    setIsDropZoneActive(false);

                    // If a baselineId was returned we want to track it so the user can retry import with same baseline
                    if (response.payload.baselineId)
                        setBaselineId(response.payload.baselineId);

                } else if(response.payload.errors && response.payload.errors.length > 0) {
                    Notification("error", "Validation Errors", response.payload.errors[0].message);
                    setIsLoading(false);
                    setIsDropZoneActive(false);

                    // If a baselineId was returned we want to track it so the user can retry import with same baseline
                    if (response.payload.baselineId)
                        setBaselineId(response.payload.baselineId);
                } else {
                    Notification("success", "Import Successful", "");

                    // Set the baselineId we just saved. Skip the isDirty mechanism because the import process already saved the baseline
                    setProjectData((existing) => ({
                        ...existing,
                        baselineIds: [response.payload.baselineId]
                    }), true);

                    if (setWizardSubStep) setWizardSubStep('c.2');    // Take user to next step

                }
            } else if (response.type === types.NEW_PROJECT_WIZARD_IMPORT_BASELINE_FAILURE) {
                Notification("error", "Error", response.payload.message);
                setIsLoading(false);
                setIsDropZoneActive(false);
            }
        } catch (error) {
            Notification("error", "Error", error);
            setIsLoading(false);
            setIsDropZoneActive(false);
        }
    }

    return (
        <div className="import-tasks-content">

            <div className="container">

                <div className="row">

                    {/* left column where steps are listed */}
                    <div className="col-6">
                        <div className="steps-panel">
                            <div className="step-heading">
                                <div className="step-heading-number">1</div>
                                <div className="step-heading-title">{`If you haven't already, download and fill out our Excel Task Template.  This allows for a quick and seamless upload of your tasks.`}</div>
                            </div>

                            <div className="step-one-details">
                                <div>
                                    <button type="button" className="pacer61-primary-btn" onClick={onDownload}><i className="fa-regular fa-cloud-arrow-down fa-lg"></i>&nbsp;&nbsp; {`Download ${importType} Date Template`}</button>
                                </div>

                                <div>
                                    <h5>Tip: If you copy/paste from another source, please use Paste Special to preserve formatting (Ctrl+Alt+V).</h5>

                                    <button type="button" className="btn pacer61-link-btn" onClick={() => setShowPasteSpecial(true)}>
                                        <i className="fa-regular fa-info-circle fa-lg"></i>What is Excel Paste Special?
                                    </button>
                                    <br />
                                    {
                                        showPasteSpecial &&
                                        <h5>{`Choose the 'Paste Special' option in Excel rather than the standard 'Paste' to maximize the template's functionality. Specifically, choosing 'Values Only' will allow any irregular values in the Excel file to be highlighted, empowering you to detect and resolve conflicts.`}</h5>
                                    }
                                    {
                                        importType === 'relative' &&
                                        <Fragment>
                                            <button type="button" className="btn pacer61-link-btn" onClick={() => setShowRelativeHelp(true)}>
                                                <i className="fa-regular fa-info-circle fa-lg"></i>What is a Relative Date?
                                            </button>
                                            <br />
                                        </Fragment>
                                    }
                                    {
                                        importType === 'static' &&
                                        <Fragment>
                                            <button type="button" className="btn pacer61-link-btn" onClick={() => setShowStaticHelp(true)}>
                                                <i className="fa-regular fa-info-circle fa-lg"></i>What is a Static Date?
                                            </button>
                                            <br />
                                        </Fragment>
                                    }
                                    {
                                        showRelativeHelp &&
                                        <h5>{`Pacer will auto-calculate task dates based on your project's end date.`}</h5>
                                    }
                                    {
                                        showStaticHelp &&
                                        <h5>All teams will complete all project tasks on the exact same dates/timeline.</h5>
                                    }
                                </div>
                            </div>

                            <div className="step-heading">
                                <div className="step-heading-number">2</div>
                                <div className="step-heading-title">Once complete, use the file uploader below to upload your Excel Task Template.</div>
                            </div>

                            <div>
                                <button type="button" className="btn pacer61-link-btn" onClick={() => chooseDiffAddTaskMethod()}>
                                    <i className="fa-regular fa-chevron-left fa-lg"></i>Select Different Add Task Method
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* right column where upload is performed */}
                    <div className="col-6">
                        <div className="upload-panel">
                            <div className="upload-panel-top">
                                <div>

                                </div>
                                <div>
                                    <input ref={hiddenBrowseButton} type="file" onChange={onFileBrowseChange} accept=".xlsx" />
                                    <button type="button" className="btn pacer61-primary-purple-btn" onClick={() => hiddenBrowseButton.current.click()}>
                                        <i className="fa-regular fa-upload"></i>&nbsp;&nbsp;Upload File
                                    </button>
                                </div>
                            </div>

                            <div className="upload-panel-bottom">
                                <DropZone noClick={true}
                                    accept={{
                                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [], // Accepts .xlsx files
                                    }}
                                    multiple={false}
                                    onDrop={onDropFile}
                                    onDragEnter={() => setIsDropZoneActive(true)}
                                    onDragLeave={() => setIsDropZoneActive(false)}
                                    >
                                    { ({ getRootProps, getInputProps, isDragActive }) => (
                                        <div {...getRootProps({ className: `dropzone ${isDragActive ? 'active' : ''}` })}>
                                            <input {...getInputProps()} />
                                            { isDragActive ? (
                                                <div className="dropzone-overlay">
                                                    <div className="overlay-content">
                                                        <Icon name="cloud-upload-alt" />
                                                        <p>Drop file here to import</p>
                                                    </div>
                                                </div>
                                            ) : (
                                                <p>Drag and drop a file here or select one below</p>
                                            )}
                                            <FileChooser
                                                name="file"
                                                onChange={onDropFile}
                                                isRequired
                                                errorMessage="A file is required"
                                                acceptTypes=".xlsx"
                                            />

                                            { importErrors.length > 0 &&
                                                <Fragment>
                                                    <div className="break"></div>
                                                    <div className="text-danger">
                                                        The following tasks are not importing correctly. Please review the messages below and resolve the issue in the Excel file. When you are done, please upload the file again. If you need to create new departments, phases, tags, or job titles, then <Link to={{ pathname: '/admin/home' }} target="_blank">click here</Link>.
                                                    </div>
                                                    <div className="break"></div>
                                                </Fragment> }
                                                
                                            { importErrors.map((importError, idx) => (
            
                                                <div className="row w-75" key={idx}>
                                                    <div className="col-3">
                                                        <span className="text-nowrap">Row {importError.rowNumber}</span>
                                                    </div>
                                                    <div className="col-3">
                                                        <span className="text-nowrap">Column {importError.columnNumber}</span>
                                                    </div>
                                                    <div className="col-6">
                                                        <span className="text-center">{importError.errorMessage}</span>
                                                    </div>
                                                </div>
                                            )) }
                                        </div>
                                    ) }
                                </DropZone>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

        </div>
    );
}

export default ImportTasks;
